import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText,
  LogOut,
  AlertTriangle,
  Plus,
  Search,
  MoreVertical,
  Copy,
  Pencil,
  Trash2,
  Clock,
  Eye,
  ChevronDown,
  Download,
  Upload,
} from 'lucide-react';
import { toast } from "sonner";
import { getTemplates, deleteTemplate, createTemplate } from '../../../services/templateService';
import { TemplatePreview } from './FormBuilder';
import { usePermission } from '@/contexts/RoleAccessContext';
import { createCanHelpers } from '@/utils/rbac';
import {
  PERFORMANCE_TEMPLATE_TYPES,
  downloadPerformanceTemplate,
  parsePerformanceTemplateFile,
} from '@/lib/performanceTemplateExcel';

const typeConfig = {
  "Appraisal": { icon: FileText, color: "bg-primary/10 text-primary", badge: "border-primary/20 text-primary bg-primary/10 dark:border-primary/80 dark:bg-primary/10 dark:text-primary" },
  "PIP": { icon: AlertTriangle, color: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400", badge: "border-orange-200 text-orange-600 bg-orange-50 dark:border-orange-800 dark:text-orange-400 dark:bg-orange-950/30" },
  "Exit Interview": { icon: LogOut, color: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400", badge: "border-red-200 text-red-600 bg-red-50 dark:border-red-800 dark:text-red-400 dark:bg-red-950/30" },
};

const SKELETON_COUNT = 6;

const TemplateCardSkeleton = () => (
  <Card className="animate-pulse" aria-hidden="true">
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between">
        <div className="h-9 w-9 rounded-lg bg-muted" />
        <div className="h-7 w-7 rounded bg-muted" />
      </div>
      <div className="h-5 w-3/4 bg-muted rounded mt-2" />
      <div className="flex gap-2 mt-2">
        <div className="h-4 w-16 rounded bg-muted" />
        <div className="h-4 w-12 rounded bg-muted" />
      </div>
    </CardHeader>
    <CardContent className="pb-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="h-12 bg-muted rounded" />
        <div className="h-12 bg-muted rounded" />
        <div className="h-12 bg-muted rounded" />
      </div>
    </CardContent>
    <CardFooter className="border-t border-border pt-3 flex justify-between">
      <div className="h-3 w-24 bg-muted rounded" />
      <div className="h-3 w-16 bg-muted rounded" />
    </CardFooter>
  </Card>
);

const TemplateLibrary = ({ onEditTemplate, onCreateNew, onImportPreview }) => {

  const { can } = usePermission();
  const { canCreate, canEdit, canDelete } = createCanHelpers(can);

  const canCreateTemplate = canCreate("template_builder");
  const canEditTemplate = canEdit("template_builder");
  const canDeleteTemplate = canDelete("template_builder");

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [templateToPreview, setTemplateToPreview] = useState(null);
  const [typeSelectModalOpen, setTypeSelectModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const importTypeRef = useRef(null);

  // Function to fetch templates - can be called on demand
  const fetchTemplates = () => {
    setLoading(true);
    getTemplates()
      .then(response => {
        const data = response.data;
        if (data && data.length > 0) {
          const enriched = data.map(t => ({
            ...t,
            id: t._id,
            _id: t._id,
            sectionCount: t.sections?.length || 0,
            questionCount: (t.sections || []).reduce((s, sec) => s + (sec.questions?.length || 0), 0),
          }));
          setTemplates(enriched);
        } else {
          setTemplates([]);
        }
      })
      .catch(error => {
        console.error('Error fetching templates:', error);
        toast.error('Error loading templates')
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTemplates();
  }, [refreshTrigger]);

  const filtered = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || t.type === filterType;
    return matchesSearch && matchesType;
  });

  const handleDuplicate = async (template) => {
    try {
      // Prepare payload for the new template
      const payload = {
        name: `${template.name} (Copy)`,
        type: template.type,
        sections: template.sections || [],
        ratingOptions: template.ratingOptions || [],
        exitReasons: template.type === "Exit Interview" ? (template.exitReasons || []) : [],
        reviewDates: template.type === "PIP" ? (template.reviewDates || []) : [],
      };

      // Call API to create the duplicate in database
      const response = await createTemplate(payload);
      const newTemplate = response.data;

      // Update local state with the new template
      const enrichedTemplate = {
        ...newTemplate,
        id: newTemplate._id,
        sectionCount: newTemplate.sections?.length || 0,
        questionCount: (newTemplate.sections || []).reduce((s, sec) => s + (sec.questions?.length || 0), 0),
      };

      setTemplates([enrichedTemplate, ...templates]);
      toast.success(`Template "${template.name}" duplicated successfully`);

      // Optionally: Navigate to Form Builder to edit the new template
      // if (onEditTemplate) {
      //   onEditTemplate(enrichedTemplate);
      // }
    } catch (error) {
      console.error('Error duplicating template:', error);
      const errorMessage = error?.response?.data?.error || error.message || 'Failed to duplicate template';
      toast.error(errorMessage);
    }
  };

  const confirmDelete = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      await deleteTemplate(templateToDelete.id);
      toast.success(`Template "${templateToDelete.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error.message || 'Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    try {
      if (!template.sections || !template.ratingOptions) {
        setTemplateToPreview(template);
      } else {
        setTemplateToPreview(template);
      }
      setPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error loading template preview:', error);
      toast.error('Failed to load template preview');
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      await downloadPerformanceTemplate(type);
      toast.success(`${type} template downloaded successfully`);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(`Failed to download ${type} template`);
    }
  };

  const triggerImport = (type) => {
    importTypeRef.current = type;
    if (importInputRef.current) {
      importInputRef.current.value = "";
      importInputRef.current.click();
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    const type = importTypeRef.current;
    if (!file || !type) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      event.target.value = "";
      return;
    }

    try {
      setImporting(true);
      const { valid, errors, template } = await parsePerformanceTemplateFile(file, type);

      if (!valid) {
        toast.error(errors[0] || "Import validation failed", {
          description:
            errors.length > 1
              ? `${errors.length} issues found. Fix them and try again.`
              : undefined,
        });
        return;
      }

      // Open the "Create New Template" form pre-filled with the imported data
      // so the user can preview and edit before saving.
      if (onImportPreview) {
        onImportPreview(template);
        toast.success(`${type} template loaded — review and save to create it`);
      } else {
        // Fallback: create directly if no preview handler is wired up.
        const response = await createTemplate(template);
        const newTemplate = response.data;
        setTemplates((prev) => [
          {
            ...newTemplate,
            id: newTemplate._id,
            sectionCount: newTemplate.sections?.length || 0,
            questionCount: (newTemplate.sections || []).reduce(
              (s, sec) => s + (sec.questions?.length || 0),
              0
            ),
          },
          ...prev,
        ]);
        toast.success(`${type} template imported successfully`);
      }
    } catch (error) {
      console.error('Error importing template:', error);
      const errorMessage =
        error?.response?.data?.error || error.message || 'Failed to import template';
      toast.error(errorMessage);
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="template-library">
      {/* Header Row */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-templates"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Filter by type */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="min-w-[160px] justify-between" data-testid="filter-type-trigger">
                {filterType === "all" ? "All Types" : filterType}
                <ChevronDown className="h-4 w-4 ml-2 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              {["all", "Appraisal", "PIP", "Exit Interview"].map((type) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={filterType === type ? "bg-primary/10 text-primary" : ""}
                  data-testid={`filter-type-${type.toLowerCase().replace(' ', '-')}`}
                >
                  {type === "all" ? "All Types" : type}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Download Template */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="justify-between gap-2" data-testid="download-template-trigger">
                <span className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Download Template
                </span>
                <ChevronDown className="h-4 w-4 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {PERFORMANCE_TEMPLATE_TYPES.map((type) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => handleDownloadTemplate(type)}
                  data-testid={`download-template-${type.toLowerCase().replace(' ', '-')}`}
                >
                  <Download className="h-3.5 w-3.5 mr-2" /> {type}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Import Template */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="justify-between gap-2"
                disabled={!canCreateTemplate || importing}
                data-testid="import-template-trigger"
              >
                <span className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  {importing ? "Importing..." : "Import Template"}
                </span>
                <ChevronDown className="h-4 w-4 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {PERFORMANCE_TEMPLATE_TYPES.map((type) => (
                <DropdownMenuItem
                  key={type}
                  onClick={() => triggerImport(type)}
                  data-testid={`import-template-${type.toLowerCase().replace(' ', '-')}`}
                >
                  <Upload className="h-3.5 w-3.5 mr-2" /> {type}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Hidden file input shared by the Import Template dropdown */}
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleImportFile}
        data-testid="import-template-input"
      />

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Create New Card */}
        <Card
          className={`border-2 border-dashed transition-all flex items-center justify-center min-h-[200px] group ${canCreateTemplate
            ? "hover:border-primary/50 hover:bg-primary/5 cursor-pointer"
            : "opacity-50 cursor-not-allowed border-muted"
            }`}
          onClick={() => {
            if (canCreateTemplate) {
              setTypeSelectModalOpen(true);
            }
          }}
          data-testid="create-new-template"
        >
          <div className="text-center text-muted-foreground group-hover:text-primary transition-colors">
            <Plus className="h-10 w-10 mx-auto mb-2 opacity-40 group-hover:opacity-100 transition-opacity" />
            <p className="font-medium">Create New Template</p>
            <p className="text-xs mt-1">Start from scratch or duplicate existing</p>
          </div>
        </Card>

        {/* Skeleton cards while loading */}
        {loading && Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <TemplateCardSkeleton key={`tpl-skeleton-${i}`} />
        ))}

        {/* Template Cards */}
        {!loading && filtered.map((template) => {
          const config = typeConfig[template.type];
          const Icon = config.icon;
          return (
            <Card
              key={template.id}
              className="hover:shadow-md transition-all group"
              data-testid={`template-card-${template.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreVertical size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          if (canEditTemplate && onEditTemplate) {
                            onEditTemplate(template);
                          }
                        }}
                        disabled={!canEditTemplate}
                        className={!canEditTemplate ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          if (canCreateTemplate) {
                            handleDuplicate(template);
                          }
                        }}
                        disabled={!canCreateTemplate}
                        className={!canCreateTemplate ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handlePreview(template)}>
                        <Eye className="h-3.5 w-3.5 mr-2" /> Preview
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className={`text-red-600 focus:text-red-600 ${!canDeleteTemplate ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        onClick={() => {
                          if (canDeleteTemplate) {
                            confirmDelete(template);
                          }
                        }}
                        disabled={!canDeleteTemplate}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <CardTitle className="text-base mt-2 line-clamp-1">{template.name}</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] ${config.badge}`}>{template.type}</Badge>
                </div>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-muted rounded">
                    <p className="text-lg font-bold text-foreground">{template.sectionCount}</p>
                    <p className="text-[10px] text-muted-foreground">Sections</p>
                  </div>
                  <div className="p-2 bg-muted rounded">
                    <p className="text-lg font-bold text-foreground">{template.questionCount}</p>
                    <p className="text-[10px] text-muted-foreground">Questions</p>
                  </div>
                  <div className="p-2 bg-muted rounded">
                    <p className="text-lg font-bold text-foreground">{template.usedIn}</p>
                    <p className="text-[10px] text-muted-foreground">Used In</p>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="border-t border-border pt-3 flex justify-between items-center text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(template.lastModified).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                <span>By {template.createdBy}</span>
              </CardFooter>
            </Card>
          );
        })}
      </div>


      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No templates to display</p>
          <p className="text-sm">Create a new template to get started, or adjust your search and filters.</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Template
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>"{templateToDelete?.name}"</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">
                <strong>Warning:</strong> This will permanently remove the template and all its versions from the system.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setTemplateToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-template"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Template Preview
            </DialogTitle>
          </DialogHeader>
          {templateToPreview && (
            <TemplatePreview
              title={templateToPreview.name}
              sections={templateToPreview.sections || []}
              ratingOptions={templateToPreview.ratingOptions || []}
              type={templateToPreview.type}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Template Type Selection Modal */}
      <Dialog open={typeSelectModalOpen} onOpenChange={setTypeSelectModalOpen}>
        <DialogContent className="max-w-2xl border border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-2xl">Choose Template Type</DialogTitle>
            <DialogDescription>
              Select the type of template you want to create
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-6">
            {/* Appraisal Card */}
            <Card
              className="cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all group border"
              onClick={() => {
                setTypeSelectModalOpen(false);
                onCreateNew && onCreateNew("Appraisal");
              }}
            >
              <CardContent className="pt-6 text-center">
                <div className="p-3 rounded-lg bg-primary/10 text-primary mx-auto w-fit mb-3 group-hover:scale-110 transition-transform">
                  <FileText className="h-8 w-8" />
                </div>
                <h3 className="font-semibold text-base mb-1">Appraisal</h3>
                <p className="text-xs text-muted-foreground">
                  Performance reviews and annual appraisals
                </p>
              </CardContent>
            </Card>

            {/* PIP Card */}
            <Card
              className="cursor-pointer hover:shadow-lg hover:border-orange-400 transition-all group border"
              onClick={() => {
                setTypeSelectModalOpen(false);
                onCreateNew && onCreateNew("PIP");
              }}
            >
              <CardContent className="pt-6 text-center">
                <div className="p-3 rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 mx-auto w-fit mb-3 group-hover:scale-110 transition-transform">
                  <AlertTriangle className="h-8 w-8" />
                </div>
                <h3 className="font-semibold text-base mb-1">PIP</h3>
                <p className="text-xs text-muted-foreground">
                  Performance improvement plans
                </p>
              </CardContent>
            </Card>

             {/* Exit Interview Card */}
            <Card
              className="cursor-pointer hover:shadow-lg hover:border-red-400 transition-all group border"
              onClick={() => {
                setTypeSelectModalOpen(false);
                onCreateNew && onCreateNew("Exit Interview");
              }}
            >
              <CardContent className="pt-6 text-center">
                <div className="p-3 rounded-lg bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 mx-auto w-fit mb-3 group-hover:scale-110 transition-transform">
                  <LogOut className="h-8 w-8" />
                </div>
                <h3 className="font-semibold text-base mb-1">Exit Interview</h3>
                <p className="text-xs text-muted-foreground">
                  Employee offboarding feedback
                </p>
              </CardContent>
            </Card>

          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TemplateLibrary;
